/**
 * RouteDocumentMeta — the single per-route owner of the document head.
 *
 * Emits, for the current route:
 *   - <html lang dir>
 *   - <title>{page} | ProperHumanDiet</title>     (brand is never translated;
 *     the pattern is identical in every language)
 *   - ONE localized <meta name="description">, plus the og:/twitter: mirrors
 *
 * Description lookup (common namespace):
 *   meta.routes.<routeName>.description   -> route specific (routeName without
 *                                            the `-i18n` suffix)
 *   meta.default.description              -> generic fallback
 *
 * Page-level override: a page that renders `<SEOHead description=...>` wins
 * over this component. Both are react-helmet-async instances and Helmet
 * de-dupes same-key tags (`name=description`, `og:*`, `twitter:*`) with the
 * later / deeper instance winning, so there is never a second tag. Tags that
 * Helmet does not own (server-injected head, static index.html) are removed
 * by `removeUnownedHeadDuplicates()` on every Helmet commit.
 *
 * Title state suffix (see `@shared/seo/documentTitleState`):
 *
 *   import { useDocumentTitleState } from "@/components/RouteDocumentMeta";
 *   useDocumentTitleState(needsKey ? t("meta.states.keyNeeded") : null);
 *   // -> "Voice Log - key needed | ProperHumanDiet"
 *
 * The argument is already-localized text; null clears it; it is cleared
 * automatically when the page unmounts.
 */
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import { removeUnownedHeadDuplicates } from "@shared/seo/headDedupe";
import { useDocumentTitleStateValue } from "@shared/seo/documentTitleState";

import {
  languages,
  normalizeLanguage,
  type SupportedLanguage,
} from "../i18n";

export {
  useDocumentTitleState,
  useDocumentTitleStateValue,
} from "@shared/seo/documentTitleState";

export const BRAND_NAME = "ProperHumanDiet";

/** English last-resort copy, used only if the catalog is missing the key. */
const DEFAULT_DESCRIPTION_FALLBACK =
  "ProperHumanDiet - guided nutrition and recovery tracking.";

interface RouteDocumentMetaRoute {
  name: string;
  navLabel?: string;
}

type TFn = ReturnType<typeof useTranslation>["t"];

export function titleizeRouteName(name: string): string {
  return name
    .replace(/-i18n$/, "")
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function getRouteDocumentTitle(
  route: RouteDocumentMetaRoute,
  t: TFn,
): string {
  const fallback = route.navLabel ?? titleizeRouteName(route.name);
  const key = `navigation.routes.${route.name.replace(/-i18n$/, "")}`;
  const translated = t(key, { defaultValue: fallback });
  return translated === key ? fallback : translated;
}

/** `{page} - {suffix} | ProperHumanDiet`, or `{page} | ProperHumanDiet`. */
export function buildDocumentTitle(
  page: string,
  stateSuffix?: string | null,
): string {
  const suffix = stateSuffix?.trim();
  return `${page}${suffix ? ` - ${suffix}` : ""} | ${BRAND_NAME}`;
}

function lookup(t: TFn, key: string): string | null {
  const value = t(key, { defaultValue: "" }) as string;
  // Missing keys resolve to "" (our defaultValue) or, with some i18next
  // configurations, to the key itself.
  return value && value !== key ? value : null;
}

export function getRouteDocumentDescription(
  route: RouteDocumentMetaRoute,
  t: TFn,
): string {
  const name = route.name.replace(/-i18n$/, "");
  return (
    lookup(t, `meta.routes.${name}.description`) ??
    lookup(t, "meta.default.description") ??
    DEFAULT_DESCRIPTION_FALLBACK
  );
}

function defaultOgImage(): string | null {
  if (typeof window === "undefined") return null;
  const base = import.meta.env?.BASE_URL ?? "/";
  return `${window.location.origin}${base}branding/og-image.png`;
}

export function RouteDocumentMeta({
  route,
}: {
  route: RouteDocumentMetaRoute;
}) {
  const { t, i18n } = useTranslation("common");
  const stateSuffix = useDocumentTitleStateValue();
  const lang = normalizeLanguage(
    i18n.resolvedLanguage ?? i18n.language,
  ) as SupportedLanguage;
  const dir = languages[lang].dir;
  const pageTitle = getRouteDocumentTitle(route, t);
  const title = buildDocumentTitle(pageTitle, stateSuffix);
  const socialTitle = buildDocumentTitle(pageTitle);
  const description = getRouteDocumentDescription(route, t);
  const ogImage = defaultOgImage();

  return (
    <Helmet onChangeClientState={() => removeUnownedHeadDuplicates()}>
      <html lang={lang} dir={dir} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={description} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
    </Helmet>
  );
}
